import { PaceCurve } from '../../charts'
import { CfoInteractiveChart } from '../../analysis'
import { CfoSegmentedControl } from '../../CfoPagePrimitives'
import { paceRailStats, selectPace } from '../ceoCockpitModel'
import { BASIS_LABEL, formatCurrencyExact } from '../ceoFormat'
import type { CeoBasisKind, CeoCockpitResponse, CeoMetricId, CeoPeriodKind } from '../ceoCockpitTypes'
import { chartWidthFor, useContainerWidth } from '../useContainerWidth'

const METRIC_PILLS: Array<{ id: CeoMetricId; label: string }> = [
  { id: 'sales', label: 'Sales' },
  { id: 'gross_profit', label: 'GP' },
  { id: 'net_income', label: 'NI' },
]

export function CeoPacePanel({
  payload,
  period,
  basis,
  metric,
  onMetricChange,
  onExplain,
}: {
  payload: CeoCockpitResponse
  period: CeoPeriodKind
  basis: CeoBasisKind
  metric: CeoMetricId
  onMetricChange: (metric: CeoMetricId) => void
  onExplain: () => void
}) {
  const [hostRef, hostWidth] = useContainerWidth<HTMLDivElement>()
  const curves = Array.isArray(payload.pace_curves) ? payload.pace_curves : []
  const curve = selectPace(payload, metric, period) || selectPace(payload, 'sales', 'mtd')
  if (!curve || !Array.isArray(curve.days) || !curve.days.length) return null
  // Draw the curve at the column's real width (720 max) so labels stay legible on a phone.
  const chartWidth = chartWidthFor(hostWidth, 720, 300)
  const chartHeight = chartWidth < 480 ? 220 : 225
  // The curve we actually drew may be a fallback for a different period; read the
  // calendar window that matches the curve, never the requested period blindly.
  const curvePeriod: CeoPeriodKind = curve.period === 'qtd' || curve.period === 'ytd' ? curve.period : 'mtd'
  const window = payload.calendar?.[curvePeriod] || payload.calendar?.mtd
  const stats = paceRailStats(curve.days, basis, window?.days_elapsed ?? 0, window?.days_in_period ?? 0)
  // Basis label for the rail: the curve has no prior-period series, so
  // "vs prior period" is compared against the prior-year ghost and says so.
  const railBasis = basis === 'prior_period' ? 'prior_year' : basis
  const basisLabel = BASIS_LABEL[railBasis]
  const options = METRIC_PILLS.filter((item) => curves.some((c) => c.metric_id === item.id))
  const periodMismatch = curvePeriod !== period

  return (
    <section className="ceo-section ceo-pace rise" id="ceo-pace" data-testid="ceo-pace" data-fg-tour="ceo-pace">
      <div className="ceo-pace-head">
        <p className="ceo-kicker">
          Pace{periodMismatch ? <span className="ceo-muted"> · showing {window?.label || curvePeriod}</span> : null}
        </p>
        {options.length > 1 ? (
          <CfoSegmentedControl
            label="Pace metric"
            value={metric}
            onChange={(value) => onMetricChange(value as CeoMetricId)}
            options={options}
          />
        ) : null}
      </div>
      <div className="ceo-pace-layout">
        <div className="ceo-pace-chart" ref={hostRef}>
          <CfoInteractiveChart hint="Ask AI to explain this pace curve" onAnalyze={onExplain} ariaLabel="Explain pace">
            <PaceCurve
              days={curve.days}
              projection={curve.projection}
              basis={railBasis}
              width={chartWidth}
              height={chartHeight}
              ariaLabel={`${metric.replace(/_/g, ' ')} ${curvePeriod} pace`}
              formatValue={(value) => formatCurrencyExact(value)}
            />
          </CfoInteractiveChart>
        </div>
        <aside className="ceo-pace-rail" data-testid="ceo-pace-rail">
          <p>
            <b>{stats.daysLeft}</b> {stats.daysLeft === 1 ? 'day' : 'days'} left
          </p>
          <p data-testid="ceo-pace-needed">
            {stats.daysLeft === 0
              ? 'Period complete'
              : stats.needed == null
                ? `Need per day vs ${basisLabel}: Not prepared`
                : `Need ${formatCurrencyExact(stats.needed)} / day to hit ${basisLabel}`}
            {stats.target != null ? <small className="ceo-muted"> · target {formatCurrencyExact(stats.target)}</small> : null}
          </p>
          <p>Best day {stats.best ? `${stats.best.date.slice(5)} · ${formatCurrencyExact(stats.best.delta)}` : 'Not prepared'}</p>
          <p data-testid="ceo-pace-py-same-day">
            Prior year same day {stats.priorYearSameDay == null ? 'Not prepared' : formatCurrencyExact(stats.priorYearSameDay)}
          </p>
        </aside>
      </div>
    </section>
  )
}
